import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import MainPage from '../features/mainPage/presentation/MainPage.vue'
import AppSettingsEditPage from '../features/appSettings/presentation/AppSettingsEditPage.vue'
import TestingPage from '../features/testing/presentation/TestingPage.vue'
import { UsersEditPage, UsersListPage, UsersNewPage, UsersProfilePage } from '../features/users/presentation'
import {
  CardIssuePointsListPage,
  CardIssuePointNewPage,
  CardIssuePointEditPage,
} from '../features/cardIssuePoints/presentation'
import { SalesPointsEditPage, SalesPointsListPage, SalesPointsNewPage } from '../features/salesPoints'
import { CitizensListPage, CitizensEditPage, CitizensNewPage } from '../features/citizens/presentation'
import { ActualIssueProductNewPage, ActualIssueProductsListPage } from '../features/actualIssueProducts/presentation'
import {
  AppAuthService,
  RolesService,
  UsersService,
  CitizensService,
  CardIssuePointsService,
  SelfRegistrationService,
  ControlQuestionsService,
  IdentityCardsService,
  MunicipalEntitiesService,
  BGOperationsService,
  AppSettingsService,
  SalesPointsService,
  ActualIssueProductsService,
  GlobalSettingsService,
  ConsumptionStandardsService,
  ConsumptionRatesService,
  CitizenCategoriesCoefficientsService,
  TranslationService,
  CoreDict,
  ConnectionStatusService,
  ProductPlansService,
  IssuanceCardsStatsService,
  UsedCardsStatsService,
  ToolsService,
  AddressSuggesterService,
} from '@prodcard/core'
import { ConsumptionStandardsListPage } from '../features/consumptionStandards/presentation'
import { ConsumptionRatesListPage } from '../features/consumptionRates/presentation'
import { CitizenCategoriesCoefficientsListPage } from '../features/citizenCategoriesCoefficients/presentation'
import { ProductPlanPage } from '../features/productPlan/presentation'
import { IssuanceCardsStatsListPage } from '../features/issuanceCardsStats/presentation'
import { ToolsPage } from '../features/tools'
import { vueRoute } from '../helpers'
import { AuthPage, PasswordChangePage, PasswordResetPage } from '../features'
import { SelfRegistrationPage } from '../features/registration'
import { UsedCardsStatListPage, UsedCardsStatNewPage } from '../features/usedCardsStat'

export function buildRootRouter(opts: {
  authService: AppAuthService
  usersService: UsersService
  rolesService: RolesService
  citizensService: CitizensService
  cardIssuePointsService: CardIssuePointsService
  salesPointsService: SalesPointsService
  selfRegistrationService: SelfRegistrationService
  controlQuestionsService: ControlQuestionsService
  identityCardsService: IdentityCardsService
  municipalEntitiesService: MunicipalEntitiesService
  bgOperationsService: BGOperationsService
  appSettingsService: AppSettingsService
  consumptionStandardsService: ConsumptionStandardsService
  productPlansService: ProductPlansService
  issuanceCardsStatsService: IssuanceCardsStatsService
  usedCardsStatsService: UsedCardsStatsService
  actualIssueProductsService: ActualIssueProductsService
  globalSettingsService: GlobalSettingsService
  consumptionRatesService: ConsumptionRatesService
  citizenCategoriesCoefficientsService: CitizenCategoriesCoefficientsService
  translationService: TranslationService<CoreDict>
  toolsService: ToolsService
  addressSuggesterService: AddressSuggesterService
  connectionStatusService?: ConnectionStatusService
}) {
  const routes: Array<RouteRecordRaw> = [
    vueRoute({
      path: '/',
      component: MainPage,
      props: {
        authService: opts.authService,
        translationService: opts.translationService,
      },
    }),
    vueRoute({
      path: '/testing',
      component: TestingPage,
      props: {
        usersService: opts.usersService,
        rolesService: opts.rolesService,
        citizenService: opts.citizensService,
        municipalEntitiesService: opts.municipalEntitiesService,
        salesPointsService: opts.salesPointsService,
      },
    }),
    {
      path: '/users',
      meta: {
        requiresAuth: true,
      },
      children: [
        vueRoute({
          path: '',
          name: 'UsersListPage',
          component: UsersListPage,
          props: {
            authService: opts.authService,
            userService: opts.usersService,
            rolesService: opts.rolesService,
            municipalEntitiesService: opts.municipalEntitiesService,
          },
        }),
        vueRoute({
          path: 'new',
          name: 'UsersNewPage',
          component: UsersNewPage,
          props: {
            usersService: opts.usersService,
            rolesService: opts.rolesService,
            cardIssuePointsService: opts.cardIssuePointsService,
            controlQuestionsService: opts.controlQuestionsService,
            municipalEntitiesService: opts.municipalEntitiesService,
            authService: opts.authService,
          },
        }),
        vueRoute({
          path: ':id/edit',
          name: 'UsersEditPage',
          component: UsersEditPage,
          props: route => ({
            usersService: opts.usersService,
            rolesService: opts.rolesService,
            cardIssuePointsService: opts.cardIssuePointsService,
            controlQuestionsService: opts.controlQuestionsService,
            municipalEntitiesService: opts.municipalEntitiesService,
            authService: opts.authService,
            id: route.params.id,
          }),
        }),
      ],
    },
    vueRoute({
      path: '/profile',
      name: 'UsersProfilePage',
      component: UsersProfilePage,
      props: {
        usersService: opts.usersService,
        rolesService: opts.rolesService,
        cardIssuePointsService: opts.cardIssuePointsService,
        controlQuestionsService: opts.controlQuestionsService,
        municipalEntitiesService: opts.municipalEntitiesService,
        authService: opts.authService,
      },
    }),
    {
      path: '/citizens',
      meta: {
        requiresAuth: true,
      },
      children: [
        vueRoute({
          path: '',
          name: 'CitizensListPage',
          component: CitizensListPage,
          props: {
            citizenService: opts.citizensService,
            municipalEntitiesService: opts.municipalEntitiesService,
            authService: opts.authService,
          },
        }),
        vueRoute({
          path: ':id/edit',
          name: 'CitizensEditPage',
          component: CitizensEditPage,
          props: route => ({
            citizensService: opts.citizensService,
            municipalEntitiesService: opts.municipalEntitiesService,
            cardIssuePointsService: opts.cardIssuePointsService,
            identityCardsService: opts.identityCardsService,
            authService: opts.authService,
            consumptionRatesService: opts.consumptionRatesService,
            consumptionStandardsService: opts.consumptionStandardsService,
            addressSuggesterService: opts.addressSuggesterService,
            id: route.params.id,
          }),
        }),
        vueRoute({
          path: 'new',
          name: 'CitizensNewPage',
          component: CitizensNewPage,
          props: route => ({
            citizensService: opts.citizensService,
            municipalEntitiesService: opts.municipalEntitiesService,
            authService: opts.authService,
            addressSuggesterService: opts.addressSuggesterService,
            redirectToBack: !!route.query.redirectToBack || false,
          }),
        }),
      ],
    },
    {
      path: '/card-issue-points',
      meta: {
        requiresAuth: true,
      },
      children: [
        vueRoute({
          path: '',
          name: 'CardIssuePointsListPage',
          component: CardIssuePointsListPage,
          props: {
            authService: opts.authService,
            cardIssuePointsService: opts.cardIssuePointsService,
            municipalEntitiesService: opts.municipalEntitiesService,
          },
        }),
        vueRoute({
          path: 'new',
          name: 'CardIssuePointNewPage',
          component: CardIssuePointNewPage,
          props: route => ({
            authService: opts.authService,
            usersService: opts.usersService,
            municipalEntitiesService: opts.municipalEntitiesService,
            cardIssuePointsService: opts.cardIssuePointsService,
            addressSuggesterService: opts.addressSuggesterService,
            resultId: route.query.resultId,
          }),
        }),
        vueRoute({
          path: ':id/edit',
          name: 'CardIssuePointEditPage',
          component: CardIssuePointEditPage,
          props: route => ({
            authService: opts.authService,
            usersService: opts.usersService,
            municipalEntitiesService: opts.municipalEntitiesService,
            cardIssuePointsService: opts.cardIssuePointsService,
            addressSuggesterService: opts.addressSuggesterService,
            id: route.params.id,
          }),
        }),
      ],
    },
    {
      path: '/sales-points',
      meta: {
        requiresAuth: true,
      },
      children: [
        vueRoute({
          path: '',
          name: 'SalesPointsListPage',
          component: SalesPointsListPage,
          props: {
            salesPointsService: opts.salesPointsService,
            municipalEntitiesService: opts.municipalEntitiesService,
            authService: opts.authService,
          },
        }),
        vueRoute({
          path: 'new',
          name: 'SalesPointsNewPage',
          component: SalesPointsNewPage,
          props: {
            authService: opts.authService,
            salesPointsService: opts.salesPointsService,
            municipalEntitiesService: opts.municipalEntitiesService,
            usersService: opts.usersService,
            addressSuggesterService: opts.addressSuggesterService,
          },
        }),
        vueRoute({
          path: ':id/edit',
          name: 'SalesPointsEditPage',
          component: SalesPointsEditPage,
          props: route => ({
            authService: opts.authService,
            salesPointsService: opts.salesPointsService,
            municipalEntitiesService: opts.municipalEntitiesService,
            usersService: opts.usersService,
            addressSuggesterService: opts.addressSuggesterService,
            id: route.params.id,
          }),
        }),
      ],
    },
    {
      path: '/actual-issue-products',
      meta: {
        requiresAuth: true,
      },
      children: [
        vueRoute({
          path: '',
          name: 'ActualIssueProductsListPage',
          component: ActualIssueProductsListPage,
          props: {
            actualIssueProductsService: opts.actualIssueProductsService,
            municipalEntitiesService: opts.municipalEntitiesService,
            globalSettingsService: opts.globalSettingsService,
            authService: opts.authService,
          },
        }),
        vueRoute({
          path: 'new',
          name: 'ActualIssueProductsNewPage',
          component: ActualIssueProductNewPage,
          props: {
            authService: opts.authService,
            actualIssueProductsService: opts.actualIssueProductsService,
            municipalEntitiesService: opts.municipalEntitiesService,
          },
        }),
      ],
    },
    {
      path: '/used-cards-stats',
      meta: {
        requiresAuth: true,
      },
      children: [
        vueRoute({
          path: '',
          name: 'UsedCardsStatListPage',
          component: UsedCardsStatListPage,
          props: {
            globalSettingsService: opts.globalSettingsService,
            municipalEntitiesService: opts.municipalEntitiesService,
            salesPointsService: opts.salesPointsService,
            usedCardsStatsService: opts.usedCardsStatsService,
            authService: opts.authService,
          },
        }),
        vueRoute({
          path: 'new',
          name: 'UsedCardsStatNewPage',
          component: UsedCardsStatNewPage,
          props: {
            municipalEntitiesService: opts.municipalEntitiesService,
            salesPointsService: opts.salesPointsService,
            usedCardsStatsService: opts.usedCardsStatsService,
            authService: opts.authService,
          },
        }),
      ],
    },
    vueRoute({
      path: '/consumption-standards',
      name: 'ConsumptionStandardsListPage',
      component: ConsumptionStandardsListPage,
      props: {
        consumptionStandardsService: opts.consumptionStandardsService,
      },
      meta: {
        requiresAuth: true,
      },
    }),
    vueRoute({
      path: '/product-plan',
      name: 'ProductPlanPage',
      component: ProductPlanPage,
      props: {
        productPlansService: opts.productPlansService,
        authService: opts.authService,
      },
      meta: {
        requiresAuth: true,
      },
    }),
    vueRoute({
      path: '/issuance-card-stats',
      name: 'IssuanceCardsStatsListPage',
      component: IssuanceCardsStatsListPage,
      props: {
        issuanceCardsStatsService: opts.issuanceCardsStatsService,
        municipalEntitiesService: opts.municipalEntitiesService,
        cardIssuePointsService: opts.cardIssuePointsService,
        authService: opts.authService,
      },
      meta: {
        requiresAuth: true,
      },
    }),
    vueRoute({
      path: '/citizen-categories-coefficients',
      name: 'CitizenCategoriesCoefficientsListPage',
      component: CitizenCategoriesCoefficientsListPage,
      props: {
        authService: opts.authService,
        citizenCategoriesCoefficientsService: opts.citizenCategoriesCoefficientsService,
      },
      meta: {
        requiresAuth: true,
      },
    }),
    vueRoute({
      path: '/consumption-rates',
      name: 'ConsumptionRatesListPage',
      component: ConsumptionRatesListPage,
      props: {
        authService: opts.authService,
        consumptionRatesService: opts.consumptionRatesService,
      },
      meta: {
        requiresAuth: true,
      },
    }),
    vueRoute({
      path: '/authorization',
      name: 'Authorization',
      component: AuthPage,
      props: {
        authService: opts.authService,
      },
      meta: {
        layout: 'empty',
      },
    }),
    vueRoute({
      path: '/app-settings',
      name: 'AppSettings',
      component: AppSettingsEditPage,
      props: {
        appSettingsService: opts.appSettingsService,
      },
    }),
    vueRoute({
      path: '/self-registration',
      name: 'SelfRegistrationPage',
      component: SelfRegistrationPage,
      props: route => ({
        authService: opts.authService,
        cardIssuePointService: opts.cardIssuePointsService,
        usersService: opts.usersService,
        selfRegistrationService: opts.selfRegistrationService,
        controlQuestionsService: opts.controlQuestionsService,
        municipalEntitiesService: opts.municipalEntitiesService,
        redirect: route.query.redirect,
      }),
      meta: {
        layout: 'empty',
      },
    }),
    vueRoute({
      path: '/password-reset',
      name: 'PasswordResetPage',
      component: PasswordResetPage,
      props: route => ({
        authService: opts.authService,
        email: route.query.email,
        redirect: route.query.redirect,
      }),
      meta: {
        layout: 'empty',
      },
    }),
    vueRoute({
      path: '/password-change',
      name: 'PasswordChange',
      component: PasswordChangePage,
      props: route => ({
        authService: opts.authService,
        usersService: opts.usersService,
        redirect: route.query.redirect,
      }),
      meta: {
        layout: 'empty',
      },
    }),
    vueRoute({
      path: '/tools',
      name: 'ToolsPage',
      component: ToolsPage,
      props: {
        toolsService: opts.toolsService,
        authService: opts.authService,
        municipalEntitiesService: opts.municipalEntitiesService,
        connectionStatusService: opts.connectionStatusService,
      },
    }),
  ]
  return createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
  })
}
